import moment from 'moment';

import { CostCenter } from '~/data/costCenters';
import { Employees } from '~/data/employees/types';
import {
  DynamicPersonnelReportData,
  DynamicPersonnelReportData_Compensation,
} from '~/data/reports';
import { convertToDateOrUndefined } from '~/utils/helpers/date';

export type PersonnelListCustomReportRow_Employee = {
  employeeId: number;
  firstName: string;
  lastName: string;
  employeeNumber: string;
  contractNumber: string;
  dateOfBirth?: Date;
  age?: number;
  currentAge?: number;
  originalStartDate?: Date;
  startDate?: Date;
  endDate?: Date;
  gender: string;
  nationality: string;
  status: string;

  bankAccount: string;
};

export type PersonnelListCustomReportRow_Employment = {
  vacationBalanceCalculation: number;
  employmentType: string;
  employmentStatus: string;
  trialPeriod: string;
  trialPeriodEndDate?: Date;
  jobGrade: string;
  currency: string;
  taxNumber: string;
  plannedEndDate?: Date;
  reasonOfFixedTerms: string;
};

export type AdditionalPersonnelGroup = {
  code: string;
  value: string | string[];
};

export type PersonnelListCustomReportRow_PersonnelGroups = {
  personnelGroup1: string;
  personnelGroup2: string;
  personnelGroup3: string;
  personnelGroup4: string;
  personnelGroup5: string;
  additionalPersonnelGroupsArray: AdditionalPersonnelGroup[];
};

export type AdditionalCostCenter = {
  employeeId: number;
  costCenterId: number;
  costCenterName: string;
  costCenterCode: string;
  costCenterPercent?: number;
  costCenterIsDefault: boolean;
};

export type PersonnelListCustomReportRow_Position = {
  jobTitle: string;
  organizationUnit: string;
  organizationUnitNumber: string;
  parentUnit: string;
  position: string;

  manager: string;
  managerNumber: string;
  alternateManager: number[];
  deputyManager: number[];

  legalEntity: string;
  costCenter: string;
  costCenterCode: string;
  additionalCostCentersArray: AdditionalCostCenter[];
  country: string;
};

export type PersonnelListCustomReportRow_WorkingHours = {
  partTimePercentage: string;
  workingHoursType: string;
  fullTimeHours: number | string;
  partTimeHours: number | string;
};

export type PersonnelListCustomReportRow_Salaries = {
  salaryType: string;
  salaryClass: string;
  changeReason: string;
  baseSalary: number;
  salaryFrequency: string;
};

export type CompensationEntry = {
  type: string;
  amount: number;
  frequency: string;
};

export type PersonnelListCustomReportRow_Compensation = {
  allowanceArray: CompensationEntry[];
  benefitsArray: CompensationEntry[];
};

export type PersonnelListCustomReportRow_SecondaryTree = {
  secondaryUnit: string;
  secondaryUnitNumber: string;
};

export type PersonnelListCustomReportRow_Contact = {
  office: string;
  phone: string;
  mobilePhone: string;
  email: string;
};

export type PersonnelListCustomReportRow_AccountDetails = {
  userName: string;
  userLanguage: string;
  roles: string;
  loginMethodSolaforce?: boolean;
  userStatus: string;
};

export type PersonnelListCustomReportRow_Misc = {
  rawData: DynamicPersonnelReportData;
};

export type PersonnelListCustomReportRow = {
  rowId: string;
} & PersonnelListCustomReportRow_Employee &
  PersonnelListCustomReportRow_Employment &
  PersonnelListCustomReportRow_PersonnelGroups &
  PersonnelListCustomReportRow_Position &
  PersonnelListCustomReportRow_WorkingHours &
  PersonnelListCustomReportRow_Salaries &
  PersonnelListCustomReportRow_Compensation &
  PersonnelListCustomReportRow_SecondaryTree &
  PersonnelListCustomReportRow_Contact &
  PersonnelListCustomReportRow_AccountDetails &
  PersonnelListCustomReportRow_Misc;

export const convertPersonnelListCustomReportDataToPersonnelListCustomReportRow =
  (props: {
    data: DynamicPersonnelReportData[];
    reportDate?: Date;
    getParentUnitName: (unitId: number) => string;
    getEmployeeById: (employeeId?: number) => Employees | undefined;
    getCostCenterById: (costCenterId: number) => CostCenter | undefined;
    getRoleTranslation: (roleCode: string) => string;
    myHRUnits: number[];
  }): PersonnelListCustomReportRow[] => {
    const {
      data,
      reportDate,
      getParentUnitName,
      getEmployeeById,
      getCostCenterById,
      getRoleTranslation,
      myHRUnits,
    } = props;

    return data.map((entry, index): PersonnelListCustomReportRow => {
      const {
        fEmpDetails: {
          fEmpId,
          fEmpProfileId,
          fEmpFirstName,
          fEmpLastName,
          fEmpPersonNumber,
          fEmpDateOfBirth,
          fEmpGender,
          fEmpNationality,
          fEmpStatus,
          fEmpContractNumber,
          fEmpOrigStartDate,
        },
        fEmpEmployment: {
          fEmpStartDate,
          fEmpEmplEndDate,
          fEmpVacBalCalcUseSixDays,
          fEmpEmploymentType,
          fEmpEmploymentStatus,
          fEmpJobGrade,
          fEmpTrialPeriodInfo,
          fEmpTrialPeriodEndDate,
          fEmpTaxNumber,
          fEmpCurrency,
          fEmpPersonnelGroups: {
            fEmpPersonnelGroup1,
            fEmpPersonnelGroup2,
            fEmpPersonnelGroup3,
            fEmpPersonnelGroup4,
            fEmpPersonnelGroup5,
          },
          fEmpAddlPG,
          fEmpPlannedEndDate,
          fEmpFixedTermReason,
        },
        fEmpPosition: {
          fEmpUnitDetails: { fOrgUnitId, fOrgUnitName, fOrgUnitNumber },
          fEmpLegalEntityDetails: { fLegalEntityName },
          fEmpCostCenterDetails: { fCostCenterName, fCostCenterCode },
          fEmpPositionFK,
          fEmpAddlCostCenters,
          fEmpJobTitle,
          fEmpCountryFK,
          fEmpManagerFK,
          fEmpPositionId,
        },
        fEmpWorkingHours,
        fEmpSalary,
        fEmpBene,
        fEmpAllow,
        fEmpContact: {
          fEmpOfficeName,
          fEmpMobilePhone,
          fEmpWorkPhone,
          fEmpWorkEmail,
        },
        fEmpIban,
        fEmpAltManager,
        fEmpDeputyManager,
        fEmpSecondTreeUnitName,
        fEmpSecondTreeUnitCode,
        fEmpAccountDetails,
      } = entry;

      const convertCompensations = (
        compensations?: DynamicPersonnelReportData_Compensation[],
      ) => {
        if (!!compensations) {
          return compensations.map(
            ({
              fCompType,
              fCompAmount,
              fCompFrequency,
            }): CompensationEntry => ({
              type: fCompType,
              amount: fCompAmount,
              frequency: fCompFrequency,
            }),
          );
        }
        return [];
      };

      const managerDetails = getEmployeeById(fEmpManagerFK);
      const managerFullName = managerDetails
        ? `${managerDetails.fEmpFirstName} ${managerDetails.fEmpLastName}`
        : '';

      const dateOfBirth = myHRUnits.includes(fOrgUnitId)
        ? convertToDateOrUndefined(fEmpDateOfBirth)
        : undefined;

      return {
        rowId: `${fEmpId}-${fEmpProfileId}-${fEmpPositionId}-${index}`,
        // Employee Details
        employeeId: fEmpId,
        firstName: fEmpFirstName,
        lastName: fEmpLastName,
        employeeNumber: fEmpPersonNumber,
        dateOfBirth,
        age:
          !!dateOfBirth && !!reportDate
            ? moment(reportDate).diff(moment(dateOfBirth), 'years')
            : undefined,
        currentAge: !!dateOfBirth
          ? moment().diff(moment(dateOfBirth), 'years')
          : undefined,
        gender: fEmpGender,
        nationality: fEmpNationality,
        status: fEmpStatus,
        originalStartDate: convertToDateOrUndefined(fEmpOrigStartDate),

        // Employment
        contractNumber: fEmpContractNumber ?? '',

        startDate: convertToDateOrUndefined(fEmpStartDate),
        endDate: convertToDateOrUndefined(fEmpEmplEndDate),
        vacationBalanceCalculation: fEmpVacBalCalcUseSixDays,
        employmentType: fEmpEmploymentType,
        employmentStatus: fEmpEmploymentStatus,
        jobGrade: fEmpJobGrade,
        trialPeriod: fEmpTrialPeriodInfo,
        trialPeriodEndDate: convertToDateOrUndefined(fEmpTrialPeriodEndDate),

        plannedEndDate: convertToDateOrUndefined(fEmpPlannedEndDate),
        reasonOfFixedTerms: fEmpFixedTermReason ?? '',
        currency: fEmpCurrency,
        taxNumber: fEmpTaxNumber,

        bankAccount: fEmpIban,

        // Personnel Groups
        personnelGroup1: fEmpPersonnelGroup1,
        personnelGroup2: fEmpPersonnelGroup2,
        personnelGroup3: fEmpPersonnelGroup3,
        personnelGroup4: fEmpPersonnelGroup4,
        personnelGroup5: fEmpPersonnelGroup5,

        // Additional Personnel Groups
        additionalPersonnelGroupsArray: !!fEmpAddlPG
          ? Object.entries(fEmpAddlPG).map(
              ([key, value]): AdditionalPersonnelGroup => ({
                code: key,
                value,
              }),
            )
          : [],

        // Position
        jobTitle: fEmpJobTitle,
        organizationUnit: fOrgUnitName,
        organizationUnitNumber: fOrgUnitNumber,
        parentUnit: getParentUnitName(fOrgUnitId),
        position: fEmpPositionFK,

        manager: managerFullName,
        managerNumber: managerDetails?.fEmpPersonNumber ?? '',
        alternateManager: fEmpAltManager,
        deputyManager: fEmpDeputyManager,

        legalEntity: fLegalEntityName,
        costCenter: fCostCenterName,
        costCenterCode: fCostCenterCode,
        additionalCostCentersArray: fEmpAddlCostCenters.map(
          (cc): AdditionalCostCenter => {
            const {
              fEmpId,
              fEmpCostCenter,
              fEmpCostCenterPercent,
              fEmpCostCenterIsDefault,
            } = cc;

            const foundCostCenter = getCostCenterById(fEmpCostCenter);

            return {
              employeeId: fEmpId,
              costCenterId: fEmpCostCenter,
              costCenterPercent: fEmpCostCenterPercent,
              costCenterName: foundCostCenter?.fCostCenterName ?? '',
              costCenterCode: foundCostCenter?.fCostCenterCode ?? '',
              costCenterIsDefault: fEmpCostCenterIsDefault,
            };
          },
        ),
        country: fEmpCountryFK,

        // Working Hours
        partTimePercentage: fEmpWorkingHours?.fEmpPtPct ?? '',
        workingHoursType: fEmpWorkingHours?.fEmpWorkHoursType ?? '',
        fullTimeHours: fEmpWorkingHours?.fEmpFtHoursPerWeek ?? '',
        partTimeHours: fEmpWorkingHours?.fEmpPtHoursPerWeek ?? '',

        // Salary
        salaryType: fEmpSalary?.fSalaryType ?? '',
        salaryClass: fEmpSalary?.fSalaryClass ?? '',
        changeReason: fEmpSalary?.fEmplReason ?? '',
        baseSalary: fEmpSalary?.fSalaryBaseSalary ?? 0,
        salaryFrequency: fEmpSalary?.fSalaryFrequency ?? '',

        // Compensation
        allowanceArray: convertCompensations(fEmpAllow),
        benefitsArray: convertCompensations(fEmpBene),

        // Secondary Tree
        secondaryUnit: fEmpSecondTreeUnitName,
        secondaryUnitNumber: fEmpSecondTreeUnitCode,

        // Contact
        office: fEmpOfficeName,
        phone: fEmpWorkPhone,
        mobilePhone: fEmpMobilePhone,
        email: fEmpWorkEmail,

        // Account Details
        userName: fEmpAccountDetails?.fUsername ?? '',
        userLanguage: fEmpAccountDetails?.fUserLang ?? '',
        roles:
          fEmpAccountDetails?.fEmpRoles?.map(getRoleTranslation).join(', ') ??
          '',
        loginMethodSolaforce: !!fEmpAccountDetails
          ? fEmpAccountDetails?.fUserLoginMethodSolaforce ?? false
          : undefined,
        userStatus: fEmpAccountDetails?.fUserStatus ?? '',

        // Misc
        rawData: entry,
      };
    });
  };
